import React from "react"
import Button from "@kit/design/button"
import { analytics } from "./analytics"

/**
 * This is an <a> that logs an analytics event before redirecting the user. It
 * should only be used for external links; internal links are already tracked by
 * our app.
 *
 * It renders a <Button> from our design system. <Button> will turn into an <a>
 * when given an `href`.
 *
 * The benefit to using this over a `<button>` is that it provides a better user
 * experience: users will be able to hover over the link to see the URL in the
 * bottom corner of their browser window, and they can also open the link in a
 * new tab by using Command+Click or by right clicking and selecting "Open in
 * new tab" (called the "context menu").
 *
 * Analytics will not be triggered when a user opens the link via the context
 * menu. This is a browser limitation. If logging an analytics event is
 * imperative, we recommend using a `<button>` instead (however, this will
 * result in a worse UX).
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.href - The URL the link should navigate to
 * @param {string} [props.eventName] - Deprecated, use eventObject and eventAction instead: The name of the analytics event to fire
 * @param {Object} [props.eventObject] - The object associated with the analytics event
 * @param {string} [props.eventAction] - The action associated with the analytics event
 * @param {Object} [props.eventProperties] - Additional properties to include in the analytics event
 * @param {React.ReactNode} props.children - The content to render inside the link
 * @param {...any} [props.props] - Additional props to pass to the Button component
 *
 * @returns {React.ReactElement} A Button component that acts as an external link with analytics
 *
 * @example
 *
 * ```js
 * <ExternalAnalyticsLink
 *   href="https://example.com"
 *   eventObject="Element"
 *   eventAction="Clicked"
 *   eventProperties={{ foo: "optional properties to pass into analytics" }}
 * >
 *   Click me!
 * </ExternalAnalyticsLink>
 * ```
 */
export function ExternalAnalyticsLink({
  href,
  eventName,
  eventObject,
  eventAction,
  eventProperties,
  children,
  ...props
}) {
  async function handleClick() {
    if (eventObject && eventAction) {
      analytics.track(eventObject, eventAction, eventProperties, {
        transport: "beacon"
      })
    } else if (eventName) {
      analytics.event(eventName, eventProperties, { transport: "beacon" })
    }
  }

  return (
    <Button {...props} href={href} onClick={handleClick}>
      {children}
    </Button>
  )
}
